import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import "./App.scss";
import Login from "./pages/Auth/Login";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  Navigate,
} from "react-router-dom";
import NavBar from "./layouts/NavBar";
import DistributerMasterList from "./pages/distributerMaster/DistributerMasterList";
import AddNewDistributer from "./pages/distributerMaster/AddNewDistributer";
import ViewDistributor from "./pages/distributerMaster/ViewDistributor";
import CustomerMasterList from "./pages/customerMaster/CustomerMasterList";
import AddNewCustomer from "./pages/customerMaster/AddNewCustomer";
import ViewCustomer from "./pages/customerMaster/ViewCustomer";
import ProjectMasterList from "./pages/projectMaster/ProjectMasterList";
import AddNewProject from "./pages/projectMaster/AddNewProject";
import CompanyMasterList from "./pages/companyMaster/CompanyMasterList";
import AddNewCompany from "./pages/companyMaster/AddNewCompany";
import ViewCompany from "./pages/companyMaster/ViewCompany";
import EnquiryList from "./pages/enquiry/EnquiryList";
import NewEnquiry from "./pages/enquiry/NewEnquiry";
import ViewEnquiry from "./pages/enquiry/ViewEnquiry";
import Quotation from "./pages/quotation/Quotation";
import QuotationGeneral from "./pages/quotation/QuotationGeneral";
import JobInHand from "./pages/quotation/JobInHand";
import Forecast from "./pages/quotation/Forecast";
import ViewQuotation from "./pages/quotation/ViewQuotation";
import UserMasterList from "./pages/userMaster/UserMasterList";
import AddNewUser from "./pages/userMaster/AddNewUser";
import ViewUser from "./pages/userMaster/ViewUser";
import ProductMasterList from "./pages/productMaster/ProductMasterList";
import AddNewProduct from "./pages/productMaster/AddNewProduct";
import ApprovalMasterList from "./pages/approvalMaster/ApprovalMasterList";
import EditApprovalMaster from "./pages/approvalMaster/EditApprovalMaster";
import DeliveryTermsList from "./pages/deliveryTerms/DeliveryTermsList";
import AddDeliveryTerms from "./pages/deliveryTerms/AddDeliveryTerms";
import PaymentTermsList from "./pages/paymentTerms/PaymentTermsList";
import AddPaymentTerms from "./pages/paymentTerms/AddPaymentTerms";
import GeneralMasterList from "./pages/generalMaster/GeneralMasterList";
import EditGeneralMaster from "./pages/generalMaster/EditGeneralMaster";
import MasterFormsNavbar from "./layouts/MasterFormsNavbar";
import SalesManagementNavbar from "./layouts/SalesManagementNavbar";
import UserManagementNavbar from "./layouts/UserManagementNavbar";
import PackingInvoiceNavbar from "./layouts/PackingInvoiceNavbar";
import OrderList from "./pages/orders/OrderList";
import AddNewOrder from "./pages/orders/AddNewOrder";
import ViewOrder from "./pages/orders/ViewOrder";
import PackingList from "./pages/packingList/PackingList";
import AddNewPacking from "./pages/packingList/AddNewPacking";
import InvoiceList from "./pages/invoiceList/InvoiceList";
import InvoiceDetails from "./pages/invoiceList/InvoiceDetails";
import AddNewInvoice from "./pages/invoiceList/AddNewInvoice";
import DeliveryNoteList from "./pages/deliveryNote/DeliveryNoteList";
import DeliveryNoteDetails from "./pages/deliveryNote/DeliveryNoteDetails";
import AddNewDeliveryNote from "./pages/deliveryNote/AddNewDeliveryNote";
import RfqList from "./pages/rfq/RfqList";
import ProductSummary from "./pages/rfq/ProductSummary";
import ProductDetails from "./pages/rfq/ProductDetails";
import ProtectedRoute from "./Routes/ProtectedRoute";
import RoleManagementList from "./pages/roleManagement/RoleManagementList";
import ViewRole from "./pages/roleManagement/ViewRole";
import AddNewUserRole from "./pages/roleManagement/AddNewUserRole";
import MultiplierMasterList from "./pages/multiplierMaster/MultiplierMasterList";
import AddNewMultiplierMaster from "./pages/multiplierMaster/AddNewMultiplierMaster";
import ViewMultiplier from "./pages/multiplierMaster/ViewMultiplier";
import PageNotAvailable from "./components/common/PageNotAvailable";
import ViewProject from "./pages/projectMaster/ViewProject";
import NotFound from "./components/common/PageNotAvailable";
import useCookies from "./hooks/useCookies";
import { ProductTabContextProvider } from "./utils/ProductTabContext";
import Dashboard from "./pages/dashboard/Dashboard";
import QuotePDFNewSummary from "./components/quotation/quotationPDF/QuotePDFNewSummary";
import OrderPDFNewSummary from "./components/orders/orderPDF/OrderPDFNewSummary";
import Layout from "./Layout";
import QuotePDFNewDetail from "./components/quotation/quotationPDF/QuotePDFNewDetail";
import OrderPDFNewDetail from "./components/orders/orderPDF/OrderPDFNewDetail";
import FactoryOrderPDFNewSummary from "./components/factoryOrder/orderPDF/OrderPDFNewSummary";
import FactoryOrderPDFNewDetail from "./components/factoryOrder/orderPDF/OrderPDFNewDetail";
import RequireAuth from "./RequireAuth";
import QuotationGeneralView from "./pages/quotation/QuotationGeneralView";
import FactoryOrderList from "./pages/factoryOrder/FactoryOrderList";
import AddNewFactoryOrder from "./pages/factoryOrder/AddNewFactoryOrder";
import ViewFactoryOrder from "./pages/factoryOrder/ViewOrder";
import NotificationsPage from "./pages/notifications/NotificationsPage";

const PRINT_TO_LIST = {
  "/pdf-summary": "/quotation",
  "/pdf-detail": "/quotation",
  "/pdf-order-summary": "/order-list",
  "/pdf-order-detail": "/order-list",
  "/pdf-factory-order-summary": "/factory-order-list",
  "/pdf-factory-order-detail": "/factory-order-list",
};

const getRedirectPath = () => {
  const last = localStorage.getItem("lastVisitedPath");
  if (!last) return "/dashboard";
  if (PRINT_TO_LIST[last]) return PRINT_TO_LIST[last];
  if (last.startsWith("/pdf-")) return "/dashboard"; // unknown print page
  return last;
};

function PathTracker() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/" && location.pathname !== "/forgot-password") {
      localStorage.setItem("lastVisitedPath", location.pathname);
    }
  }, [location]);

  return null;
}

function App() {
  const { getCookie } = useCookies();

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    return !!token;
  });

  useEffect(() => {

    const checkAuth = () => {

      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");

      setIsAuthenticated(!!token);
    };

    window.addEventListener("storage", checkAuth);

    window.addEventListener("authChange", checkAuth);

    checkAuth();

    return () => {
      window.removeEventListener("storage", checkAuth);
      window.removeEventListener("authChange", checkAuth);
    };

  }, []);
  function NoMatch() {
    return <NotFound />;
  }

  return (
    <ProductTabContextProvider>
      <Router>
        <PathTracker />
        <Layout>
          <div className="App">
            <Routes>
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    <Navigate to={getRedirectPath()} replace />
                  ) : (
                    <Login />
                  )
                }
              />

              <Route exact path="/forgot-password" element={<ForgotPassword />} />
              <Route element={<RequireAuth />}>
                <Route element={<ProtectedRoute />}>
                  <Route
                    exact
                    path="/master-forms-nav-bar"
                    element={<MasterFormsNavbar />}
                  />
                  <Route
                    exact
                    path="/sales-management-nav-bar"
                    element={<SalesManagementNavbar />}
                  />
                  <Route
                    exact
                    path="/user-management-nav-bar"
                    element={<UserManagementNavbar />}
                  />
                  <Route
                    exact
                    path="/packing-invoice-nav-bar"
                    element={<PackingInvoiceNavbar />}
                  />
                  <Route
                    exact
                    path="/distributor-master"
                    element={<DistributerMasterList />}
                  />
                  <Route
                    exact
                    path="/add-new-distributor"
                    element={<AddNewDistributer />}
                  />
                  <Route
                    exact
                    path="/view-distributor"
                    element={<ViewDistributor />}
                  />
                  <Route
                    exact
                    path="/customer-master"
                    element={<CustomerMasterList />}
                  />
                  <Route
                    exact
                    path="/add-new-customer"
                    element={<AddNewCustomer />}
                  />
                  <Route exact path="/view-customer" element={<ViewCustomer />} />
                  <Route
                    exact
                    path="/project-master"
                    element={<ProjectMasterList />}
                  />
                  <Route
                    exact
                    path="/add-new-project"
                    element={<AddNewProject />}
                  />
                  <Route exact path="/view-project" element={<ViewProject />} />
                  <Route
                    exact
                    path="/company-master"
                    element={<CompanyMasterList />}
                  />
                  <Route
                    exact
                    path="/add-new-company"
                    element={<AddNewCompany />}
                  />
                  <Route exact path="/view-company" element={<ViewCompany />} />
                  
                  <Route
                    exact
                    path="/delivery-terms"
                    element={<DeliveryTermsList />}
                  />
                  <Route
                    exact
                    path="/add-delivery-terms"
                    element={<AddDeliveryTerms />}
                  />
                  <Route
                    exact
                    path="/multiplier-master"
                    element={<MultiplierMasterList />}
                  />
                  <Route
                    exact
                    path="/add-new-multiplier"
                    element={<AddNewMultiplierMaster />}
                  />
                  <Route
                    exact
                    path="/view-multiplier"
                    element={<ViewMultiplier />}
                  />
                  <Route
                    exact
                    path="/payment-terms"
                    element={<PaymentTermsList />}
                  />
                  <Route
                    exact
                    path="/add-payment-terms"
                    element={<AddPaymentTerms />}
                  />
                  <Route
                    exact
                    path="/general-master"
                    element={<GeneralMasterList />}
                  />
                  <Route
                    exact
                    path="/edit-general-master"
                    element={<EditGeneralMaster />}
                  />
                  <Route
                    exact
                    path="/approval-master"
                    element={<ApprovalMasterList />}
                  />
                  <Route
                    exact
                    path="/edit-approval-master"
                    element={<EditApprovalMaster />}
                  />
                  <Route exact path="/enquiry-list" element={<EnquiryList />} />
                  <Route exact path="/add-new-enquiry" element={<NewEnquiry />} />
                  <Route exact path="/view-enquiry" element={<ViewEnquiry />} />
                  <Route exact path="/order-list" element={<OrderList />} />
                  <Route exact path="/add-new-order" element={<AddNewOrder />} />
                  <Route exact path="/view-order" element={<ViewOrder />} />
                  <Route exact path="/factory-order-list" element={<FactoryOrderList />} />
                  <Route exact path="/add-new-factory-order" element={<AddNewFactoryOrder />} />
                  <Route exact path="/view-factory-order" element={<ViewFactoryOrder />} />
                  <Route exact path="/quotation" element={<Quotation />} />
                  <Route
                    exact
                    path="/quotation-general"
                    element={<QuotationGeneral />}
                  />
                  <Route
                    exact
                    path="/quotation-general-view"
                    element={<QuotationGeneralView />}
                  />
                  <Route exact path="/job-in-hand" element={<JobInHand />} />
                  <Route exact path="/forecast" element={<Forecast />} />
                  <Route exact path="/view-quotation" element={<ViewQuotation />} />
                  <Route exact path="/user-master" element={<UserMasterList />} />
                  <Route exact path="/add-new-user" element={<AddNewUser />} />
                  <Route exact path="/view-user" element={<ViewUser />} />
                  <Route
                    exact
                    path="/add-new-user-role"
                    element={<AddNewUserRole />}
                  />
                  <Route
                    exact
                    path="/role-management"
                    element={<RoleManagementList />}
                  />
                  <Route exact path="/view-role" element={<ViewRole />} />
                  <Route
                    exact
                    path="/product-master"
                    element={<ProductMasterList />}
                  />
                  <Route
                    exact
                    path="/add-new-product"
                    element={<AddNewProduct />}
                  />
                  <Route exact path="/packing-list" element={<PackingList />} />
                  <Route
                    exact
                    path="/add-new-packing"
                    element={<AddNewPacking />}
                  />
                  <Route exact path="/invoice-list" element={<InvoiceList />} />
                  <Route
                    exact
                    path="/invoice-details"
                    element={<InvoiceDetails />}
                  />
                  <Route
                    exact
                    path="/add-new-invoice"
                    element={<AddNewInvoice />}
                  />
                  <Route
                    exact
                    path="/delivery-note-list"
                    element={<DeliveryNoteList />}
                  />
                  <Route
                    exact
                    path="/delivery-note-details"
                    element={<DeliveryNoteDetails />}
                  />
                  <Route
                    exact
                    path="/add-new-delivery-note"
                    element={<AddNewDeliveryNote />}
                  />
                  <Route exact path="/rfq-list" element={<RfqList />} />
                  <Route
                    exact
                    path="/product-summary"
                    element={<ProductSummary />}
                  />
                  <Route
                    exact
                    path="/product-details"
                    element={<ProductDetails />}
                  />
                  <Route
                    exact
                    path="/dashboard"
                    element={<Dashboard />}
                  />
                  <Route exact path="/notifications" element={<NotificationsPage />} />
                  <Route
                    exact
                    path="/pdf-summary"
                    element={<QuotePDFNewSummary />}
                  />
                  <Route
                    exact
                    path="/pdf-detail"
                    element={<QuotePDFNewDetail />}
                  />
                  <Route
                    exact
                    path="/pdf-order-summary"
                    element={<OrderPDFNewSummary />}
                  />

                  <Route
                    exact
                    path="/pdf-order-detail"
                    element={<OrderPDFNewDetail />}
                  />
                  <Route
                    exact
                    path="/pdf-factory-order-summary"
                    element={<FactoryOrderPDFNewSummary />}
                  />

                  <Route
                    exact
                    path="/pdf-factory-order-detail"
                    element={<FactoryOrderPDFNewDetail />}
                  />
                  <Route exact path="/404" element={<PageNotAvailable />} />
                </Route>
                <Route path="*" element={<NoMatch />} />
              </Route>
            </Routes>
          </div>
        </Layout>
      </Router>
    </ProductTabContextProvider>
  );
}

export default App;
